import {useCallback, useEffect, useMemo, useState} from 'react';

import {countryService} from '@/services/countryService';
import type {AfriexCountry, AfriexQueryParams} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const PAGE_SIZE = 10;

const useCountryUpdate = () => {
  const [fullList, setFullList] = useState<AfriexCountry[]>([]);
  const [item, setItem] = useState<AfriexCountry>({} as AfriexCountry);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState(null);
  const [shouldShowForm, setShouldShowForm] = useState<boolean>(false);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const clearFilters = () => {
    setCurrentFilters({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  // The status filter still goes to the server (fewer records to page
  // through while preloading), but the search box does not: the whole
  // (filtered) catalogue is already in memory below, so a typed term filters
  // it directly rather than needing a `search`/`name` param the country
  // endpoint does not have.
  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return fullList;
    }
    return fullList.filter(
      (country) =>
        country.name?.toLowerCase().includes(term) ||
        country.iso2?.toLowerCase().includes(term),
    );
  }, [fullList, searchTerm]);

  const isSearch = Boolean(searchTerm.trim());

  const list = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const fetchList = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setIsLoading(true);
        // Downloading the whole collection is deliberate here: there is one row
        // per country (`iso2` is limited to AfriexCountryCode, well under 150),
        // so this loop runs once or twice and the screen can then search, sort
        // and page the full list locally.
        //
        // Do not copy this loop for a collection that grows (transactions,
        // fees, users, audit events). Every page re-runs the server's count
        // and a longer skip, so each page costs more than the last, and the
        // browser ends up holding every row. For those, page on the server and
        // render one page at a time, stream the full range from one cursor
        // (the `?action=DOWNLOAD` exports built on streamCursorToConsumer), or,
        // in a server job, read in `_id` batches the way
        // BatchTransactionProcessor does.
        const allItemsMap = new Map<string, AfriexCountry>();
        let currentPageIndex = 0;
        let hasMore = true;
        let itemsTotal = 0;

        while (hasMore) {
          const response = await countryService.getList({
            ...params,
            ...currentFilters,
            page: currentPageIndex,
            limit: 100,
          });

          const items = response.data || [];
          itemsTotal = response.total || 0;

          items.forEach((item) => {
            const key = item.id || item.iso2;
            allItemsMap.set(key, item);
          });

          if (
            allItemsMap.size >= itemsTotal ||
            items.length === 0 ||
            items.length < 100
          ) {
            hasMore = false;
          } else {
            currentPageIndex++;
          }
        }

        const sortedList = Array.from(allItemsMap.values()).sort((a, b) =>
          a?.name?.localeCompare(b.name),
        );

        setFullList(sortedList);
        setPage(0);
      } catch (e) {
        showErrorMessage(
          `Fetching country list failed:${getErrorMessage(e, String(e))}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const fetchItem = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const item = await countryService.getItem(id);
      setItem(item);
    } catch (e) {
      showErrorMessage(
        `Fetching country details failed:${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // `fetchList` try/catches into showErrorMessage, so this only fires if that
    // reporting itself threw. Attached so an empty country list cannot end up
    // looking like a loaded one; dev logger rather than a second toast, since a
    // toast is what just failed.
    fetchList({}).catch(logger.error);
  }, [fetchList]);

  const handleUpdate = useCallback(
    async (countryId: string, body: Partial<AfriexCountry>) => {
      try {
        if (!body) {
          return;
        }

        setError(null);
        setIsLoading(true);
        const country = await countryService.saveOne(countryId, body);
        if (country) {
          showSuccessMessage('Country updated successfully');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          // The endpoint responds 200 with an empty body when the `_id`
          // filter matches no country (stale id, deleted country), so a
          // resolved-but-falsy response means nothing was saved.
          showErrorMessage('Updating country failed: country not found');
        }
      } catch (e) {
        showErrorMessage(
          `Updating country failed:${getErrorMessage(e, String(e))}`,
        );
      }
      setIsLoading(false);
    },
    [],
  );

  const toggleForm = useCallback(() => {
    setShouldShowForm(!shouldShowForm);
  }, [shouldShowForm]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(0);
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    error,
    isLoading,
    isSearch,
    handleUpdate,
    fetchList,
    pageInfo: {
      page,
      total: filteredList.length,
    },
    setPage,
    list,
    item,
    fetchItem,
    shouldShowForm,
    toggleForm,
    currentFilters, // automatically triggers fetch
    filterCount,
    isPageReset,
    selectCurrentFilters,
    clearFilters,
    searchTerm,
    handleSearch,
  };
};

export default useCountryUpdate;
